import { DatePipe } from '@angular/common';
import { AfterViewChecked, AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ShowLobbyService } from '../../services/show-lobby.service';
import { PreparedShow } from '../../models/show-lobby.model';

import { appEnvironment } from '../../app-environment';

type LobbyParticipantRole = 'player' | 'moderator';
type LobbyStatus = 'waiting' | 'started';
type SocketStatus = 'offline' | 'connecting' | 'connected';
type QuizRevealStage = 'hidden' | 'question' | 'hint1' | 'hint2' | 'solution';
type QuizGamePhase = 'idle' | 'estimating' | 'betting' | 'waitingHint1' | 'waitingHint2' | 'waitingSolution' | 'waitingPayout' | 'payout';

interface LobbyParticipant {
  id: string;
  name: string;
  role: LobbyParticipantRole;
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  cameraDeviceId: string;
  microphoneDeviceId: string;
  cameraDeviceLabel: string;
  microphoneDeviceLabel: string;
  avatarImageDataUrl?: string | null;
  joinedAt: string;
}

interface DeviceOption {
  deviceId: string;
  label: string;
}

interface QuizpokerGameState {
  revealStage: QuizRevealStage;
  gamePhase: QuizGamePhase;
  activeQuestionIndex: number;
  currentTurnPlayerId: string;
  currentPot: number;
  chips: Record<string, number>;
  estimates: Record<string, string>;
  smallBlindPlayerId: string;
  bigBlindPlayerId: string;
  bettingRound: number;
  currentBet: number;
  currentRoundBets: Record<string, number>;
  foldedPlayerIds: string[];
  outPlayerIds: string[];
  actedPlayerIds: string[];
  revealedEstimates: boolean;
  dealerOffset: number;
  smallBlindValue: number;
  bigBlindValue: number;
  winnerPlayerIds: string[];
}

interface LobbySocketStateMessage {
  type: 'state';
  lobbyId: number;
  status: LobbyStatus;
  participants: LobbyParticipant[];
  gameState?: QuizpokerGameState | null;
}

interface LobbySocketErrorMessage {
  type: 'error';
  message: string;
}

interface LobbySocketWebRtcSignalMessage {
  type: 'webrtcSignal';
  fromParticipantId: string;
  toParticipantId: string;
  signalType: 'offer' | 'answer' | 'ice-candidate';
  payload: RTCSessionDescriptionInit | RTCIceCandidateInit;
}

@Component({
  selector: 'app-show-lobby-room',
  standalone: true,
  imports: [FormsModule, RouterLink, DatePipe],
  template: `
    <section class="section show-room-page" [class.quizpoker-mode]="roomStatus() === 'started' && show()?.format === 'quizpoker'">
      @if (!(roomStatus() === 'started' && show()?.format === 'quizpoker')) {
        <a routerLink="/show-lobby" class="back-link">← Zurück zur Show Lobby</a>
      }

      @if (isLoading()) {
        <article class="card accent-card room-state-card"><h2>Lobby lädt...</h2></article>
      } @else if (errorMessage()) {
        <article class="card accent-card room-state-card">
          <h2>Lobby nicht gefunden</h2>
          <p>{{ errorMessage() }}</p>
          <a routerLink="/show-lobby" class="button primary">Zur Show Lobby</a>
        </article>
      } @else if (show()) {
        @if (roomStatus() === 'started' && show()!.format === 'quizpoker') {
          <section class="quizpoker-stage">
            <div class="quizpoker-stage-head">
              <div>
                <small>Quizpoker</small>
                <h1>{{ show()!.title }}</h1>
              </div>
              <span>{{ participants().length }} verbunden</span>
            </div>

            <div class="quizpoker-board">
              <aside class="quizpoker-side left">
                @for (participant of leftQuizPlayers(); track participant.id; let i = $index) {
                  <article class="quizpoker-player-card" [class.active-turn]="currentTurnPlayerId() === participant.id" [class.round-winner]="shouldShowWinnerCrown(participant.id)">
                    <span class="seat-number">{{ i + 1 }}</span>
                    @if (shouldShowWinnerCrown(participant.id)) { <span class="winner-crown" title="Rundengewinner">♛</span> }
                    <div class="quiz-player-media">
                      @if (hasLiveVideo(participant.id)) {
                        <video #participantVideo class="quiz-live-video" [attr.data-participant-id]="participant.id" [muted]="participant.id === participantIdValue()" autoplay playsinline></video>
                      } @else if (participant.avatarImageDataUrl) {
                        <img [src]="participant.avatarImageDataUrl" [alt]="participant.name" />
                      } @else {
                        <div class="quiz-avatar">{{ participant.name.slice(0, 1).toUpperCase() }}</div>
                      }
                    </div>
                    <div class="blind-row">
                      @if (smallBlindPlayerId() === participant.id) { <span class="blind-badge">Small Blind</span> }
                      @if (bigBlindPlayerId() === participant.id) { <span class="blind-badge big">Big Blind</span> }
                      @if (isFolded(participant.id)) { <span class="blind-badge folded">Fold</span> }
                      @if (isPlayerOut(participant.id)) { <span class="blind-badge out">Raus</span> }
                    </div>
                    <input class="quiz-estimate-input" type="text" [value]="estimateDisplayFor(participant.id)" [readonly]="!canEditEstimate(participant.id)" (input)="setEstimate(participant.id, $any($event.target).value)" placeholder="Schätzung" />
                    <strong class="quiz-chip-count">{{ chipsFor(participant.id) }} Chips</strong>
                    <span class="quiz-player-name">{{ participant.name }}</span>
                  </article>
                }
              </aside>

              <main class="quizpoker-center">
                <article class="quizpoker-moderator-card">
                  <small>Moderator</small>
                  <div class="moderator-media-row">
                    @if (quizModerator() && hasLiveVideo(quizModerator()!.id)) {
                      <video #participantVideo class="quiz-moderator-video" [attr.data-participant-id]="quizModerator()!.id" [muted]="quizModerator()!.id === participantIdValue()" autoplay playsinline></video>
                    } @else if (quizModerator()?.avatarImageDataUrl) {
                      <img [src]="quizModerator()!.avatarImageDataUrl!" [alt]="quizModerator()!.name" />
                    } @else {
                      <span>{{ quizModerator()?.name?.slice(0, 1)?.toUpperCase() || 'M' }}</span>
                    }
                  </div>
                  <strong>{{ quizModerator()?.name || show()!.host }}</strong>
                </article>

                <article class="quizpoker-blind-card" [class.editable]="currentParticipant()?.role === 'moderator' && gamePhase() === 'payout'">
                  <div>
                    <small>Small Blind</small>
                    @if (currentParticipant()?.role === 'moderator' && gamePhase() === 'payout') {
                      <input type="number" min="0" [(ngModel)]="smallBlindDraft" (focus)="isEditingBlinds.set(true)" name="smallBlindDraft" />
                    } @else {
                      <strong>{{ smallBlindValue() }}</strong>
                    }
                  </div>
                  <div>
                    <small>Big Blind</small>
                    @if (currentParticipant()?.role === 'moderator' && gamePhase() === 'payout') {
                      <input type="number" min="0" [(ngModel)]="bigBlindDraft" (focus)="isEditingBlinds.set(true)" name="bigBlindDraft" />
                    } @else {
                      <strong>{{ bigBlindValue() }}</strong>
                    }
                  </div>
                  @if (currentParticipant()?.role === 'moderator' && gamePhase() === 'payout') {
                    <button type="button" class="blind-apply-button" (click)="applyBlindDraft()">Blinds übernehmen</button>
                  }
                </article>

                <article class="quizpoker-pot-card">
                  <small>Aktueller Pot</small>
                  <strong>{{ currentPot() }} Chips</strong>
                </article>

                <div class="quizpoker-reveal-stack">
                  <article class="quizpoker-reveal-card" [class.hidden-content]="!isQuestionVisible()">
                    <small>Frage</small>
                    <strong>{{ isQuestionVisible() ? quizQuestionText() : 'Frage noch verdeckt' }}</strong>
                  </article>
                  <article class="quizpoker-reveal-card" [class.hidden-content]="!isHint1Visible()">
                    <small>Hint 1</small>
                    <strong>{{ isHint1Visible() ? quizHint(1) : 'Hint 1 noch verdeckt' }}</strong>
                  </article>
                  <article class="quizpoker-reveal-card" [class.hidden-content]="!isHint2Visible()">
                    <small>Hint 2</small>
                    <strong>{{ isHint2Visible() ? quizHint(2) : 'Hint 2 noch verdeckt' }}</strong>
                  </article>
                  <article class="quizpoker-reveal-card solution" [class.hidden-content]="!isSolutionVisible()">
                    <small>Lösung</small>
                    <strong>{{ isSolutionVisible() ? quizSolution() : 'Lösung noch verdeckt' }}</strong>
                  </article>
                </div>

                <section class="quizpoker-action-zone">
                  @if (currentParticipant()?.role === 'moderator') {
                    <div class="moderator-actions">
                      <button type="button" class="button secondary" [disabled]="!canUndo()" (click)="undoLastAction()">Zurück</button>
                      <button type="button" class="button secondary" [disabled]="!canModeratorRevealQuestion()" (click)="revealQuestion()">Frage einblenden</button>
                      <button type="button" class="button secondary" [disabled]="gamePhase() !== 'waitingHint1'" (click)="revealHint1()">Hint 1 einblenden</button>
                      <button type="button" class="button secondary" [disabled]="gamePhase() !== 'waitingHint2'" (click)="revealHint2()">Hint 2 einblenden</button>
                      <button type="button" class="button secondary" [disabled]="gamePhase() !== 'waitingSolution'" (click)="revealSolution()">Lösung einblenden</button>
                      <button type="button" class="button primary" [disabled]="gamePhase() !== 'waitingPayout'" (click)="distributePot()">Pot verteilen</button>
                    </div>

                    <div class="moderator-control-row">
                      <label>
                        <span>Spieler am Zug</span>
                        <select [ngModel]="currentTurnPlayerId()" (ngModelChange)="setCurrentTurnPlayer($event)" name="currentTurnPlayer">
                          @for (participant of quizPlayers(); track participant.id) {
                            <option [value]="participant.id">{{ participant.name }}</option>
                          }
                        </select>
                      </label>
                      <label>
                        <span>Chips korrigieren</span>
                        <input type="number" min="0" [ngModel]="chipsFor(currentTurnPlayerId())" (ngModelChange)="setChips(currentTurnPlayerId(), $event)" name="chipOverride" />
                      </label>
                    </div>
                  } @else {
                    <div class="player-actions">
                      @if (canSubmitEstimate()) {
                        <button type="button" class="button primary" (click)="submitOwnEstimate()">Schätzung abgeben</button>
                      }
                      @if (isOwnTurn()) {
                        <button type="button" class="button secondary" (click)="checkOrCall()">{{ checkCallLabel() }}</button>
                        <button type="button" class="button secondary" (click)="fold()">Fold</button>
                        <label class="raise-box">
                          <span>Raise</span>
                          <input type="number" min="0" [(ngModel)]="raiseAmount" name="raiseAmount" />
                        </label>
                        <button type="button" class="button primary" (click)="raise()">Raise setzen</button>
                      } @else {
                        <span class="turn-info">{{ playerActionHint() }}</span>
                      }
                    </div>
                  }
                </section>
              </main>

              <aside class="quizpoker-side right">
                @for (participant of rightQuizPlayers(); track participant.id; let i = $index) {
                  <article class="quizpoker-player-card" [class.active-turn]="currentTurnPlayerId() === participant.id" [class.round-winner]="shouldShowWinnerCrown(participant.id)">
                    <span class="seat-number">{{ leftQuizPlayers().length + i + 1 }}</span>
                    @if (shouldShowWinnerCrown(participant.id)) { <span class="winner-crown" title="Rundengewinner">♛</span> }
                    <div class="quiz-player-media">
                      @if (hasLiveVideo(participant.id)) {
                        <video #participantVideo class="quiz-live-video" [attr.data-participant-id]="participant.id" [muted]="participant.id === participantIdValue()" autoplay playsinline></video>
                      } @else if (participant.avatarImageDataUrl) {
                        <img [src]="participant.avatarImageDataUrl" [alt]="participant.name" />
                      } @else {
                        <div class="quiz-avatar">{{ participant.name.slice(0, 1).toUpperCase() }}</div>
                      }
                    </div>
                    <div class="blind-row">
                      @if (smallBlindPlayerId() === participant.id) { <span class="blind-badge">Small Blind</span> }
                      @if (bigBlindPlayerId() === participant.id) { <span class="blind-badge big">Big Blind</span> }
                      @if (isFolded(participant.id)) { <span class="blind-badge folded">Fold</span> }
                      @if (isPlayerOut(participant.id)) { <span class="blind-badge out">Raus</span> }
                    </div>
                    <input class="quiz-estimate-input" type="text" [value]="estimateDisplayFor(participant.id)" [readonly]="!canEditEstimate(participant.id)" (input)="setEstimate(participant.id, $any($event.target).value)" placeholder="Schätzung" />
                    <strong class="quiz-chip-count">{{ chipsFor(participant.id) }} Chips</strong>
                    <span class="quiz-player-name">{{ participant.name }}</span>
                  </article>
                }
              </aside>
            </div>
          </section>
        } @else {
        <div class="page-title-block room-heading">
          <h1>{{ show()!.title }}</h1>
          <span class="title-line"></span>
        </div>

        <section class="room-summary-grid">
          <article class="card accent-card room-info-card">
            <small>Lobby</small>
            <h2>{{ formatTitle(show()!.format) }}</h2>
            <p>{{ show()!.date | date:'dd.MM.yyyy' }} · Host: {{ show()!.host }}</p>
            <div class="room-code-line">
              <span>Code</span>
              <strong>{{ show()!.lobbyCode }}</strong>
            </div>
          </article>

          <article class="card accent-card room-start-card" [class.started]="roomStatus() === 'started'">
            <small>Status</small>
            <h2>{{ roomStatus() === 'started' ? 'Gestartet' : 'Wartet auf Start' }}</h2>
            <p>Bis zum Start ist diese Lobby für alle Spielshows gleich. Danach wechselt die App in den spezifischen Show-Modus.</p>
            <div class="socket-pill" [class.connected]="socketStatus() === 'connected'">
              {{ socketStatus() === 'connected' ? 'Live verbunden' : socketStatus() === 'connecting' ? 'Verbinde...' : 'Offline' }}
            </div>
            @if (currentParticipant()?.role === 'moderator') {
              <button class="button primary" type="button" [disabled]="roomStatus() === 'started'" (click)="startShow()">
                {{ roomStatus() === 'started' ? 'Show läuft' : 'Spielshow starten' }}
              </button>
            } @else {
              <span class="moderator-hint">Nur der Moderator kann die Spielshow starten.</span>
            }
          </article>
        </section>

        <section class="room-layout">
          <article class="card accent-card room-settings-card">
            <div class="room-card-head">
              <small>Setup</small>
              <h2>Deine Lobby-Einstellungen</h2>
            </div>

            <form class="room-settings-form" (ngSubmit)="joinRoom()">
              <label>
                <span>Name</span>
                <input type="text" [(ngModel)]="joinName" name="joinName" placeholder="Dein Name" />
              </label>

              <label>
                <span>Rolle</span>
                <select [(ngModel)]="joinRole" name="joinRole">
                  <option value="player">Mitspieler</option>
                  <option value="moderator" [disabled]="hasOtherModerator()">Moderator</option>
                </select>
              </label>

              @if (hasOtherModerator() && joinRole !== 'moderator') {
                <p class="room-helper">Diese Lobby hat bereits einen Moderator.</p>
              }

              <div class="device-toggle-grid">
                <button type="button" class="choice-chip big-choice" [class.active]="cameraEnabled" (click)="toggleCamera()">
                  {{ cameraEnabled ? 'Cam aktiv' : 'Cam aus' }}
                </button>
                <button type="button" class="choice-chip big-choice" [class.active]="microphoneEnabled" (click)="toggleMicrophone()">
                  {{ microphoneEnabled ? 'Mic aktiv' : 'Mic aus' }}
                </button>
              </div>

              <label>
                <span>Kamera</span>
                <select [(ngModel)]="selectedCamera" name="selectedCamera" [disabled]="!cameraEnabled" (ngModelChange)="refreshPreview()">
                  @for (camera of cameraOptions(); track camera.deviceId) {
                    <option [value]="camera.deviceId">{{ camera.label }}</option>
                  }
                </select>
              </label>

              @if (canUseImageFallback()) {
                <label class="avatar-upload">
                  <span>Bild statt Kamera</span>
                  <input type="file" accept="image/*" (change)="handleAvatarUpload($event)" />
                </label>
              }

              <label>
                <span>Mikrofon</span>
                <select [(ngModel)]="selectedMicrophone" name="selectedMicrophone" [disabled]="!microphoneEnabled" (ngModelChange)="refreshPreview()">
                  @for (microphone of microphoneOptions(); track microphone.deviceId) {
                    <option [value]="microphone.deviceId">{{ microphone.label }}</option>
                  }
                </select>
              </label>

              @if (mediaError()) {
                <p class="room-helper media-error">{{ mediaError() }}</p>
              }

              @if (lobbyError()) {
                <p class="room-helper media-error">{{ lobbyError() }}</p>
              }

              <button class="button primary" type="submit" [disabled]="socketStatus() !== 'connected'">
                {{ currentParticipant() ? 'Einstellungen speichern' : 'Lobby beitreten' }}
              </button>
            </form>
          </article>

          <article class="card accent-card room-preview-card">
            <div class="camera-preview" [class.disabled]="!cameraEnabled || selectedCamera === 'none'" [class.has-video]="cameraEnabled && selectedCamera !== 'none' && hasPreviewStream()" [class.has-avatar]="!!avatarImageDataUrl && canUseImageFallback()">
              <video #previewVideo class="preview-video" autoplay muted playsinline [class.hidden]="!cameraEnabled || selectedCamera === 'none' || !hasPreviewStream()"></video>

              @if (avatarImageDataUrl && canUseImageFallback()) {
                <img class="preview-avatar-image" [src]="avatarImageDataUrl" alt="Ausgewähltes Lobby-Bild" />
                <div class="preview-overlay">
                  <strong>{{ joinName || 'Vorschau' }}</strong>
                  <small>Bild statt Kamera</small>
                </div>
              } @else if (!cameraEnabled) {
                <div class="preview-placeholder">
                  <span>CAM OFF</span>
                  <strong>{{ joinName || 'Vorschau' }}</strong>
                  <small>Optional Bild hochladen</small>
                </div>
              } @else if (selectedCamera === 'none') {
                <div class="preview-placeholder">
                  <span>CAM</span>
                  <strong>{{ joinName || 'Vorschau' }}</strong>
                  <small>Keine Kamera ausgewählt</small>
                </div>
              } @else if (!hasPreviewStream()) {
                <div class="preview-placeholder">
                  <span>CAM</span>
                  <strong>{{ joinName || 'Vorschau' }}</strong>
                  <small>{{ selectedCameraLabel() }}</small>
                </div>
              }

              @if (cameraEnabled && selectedCamera !== 'none' && hasPreviewStream()) {
                <div class="preview-overlay">
                  <strong>{{ joinName || 'Vorschau' }}</strong>
                  <small>{{ selectedCameraLabel() }}</small>
                </div>
              }
            </div>

            <div class="microphone-preview" [class.disabled]="!microphoneEnabled">
              <div>
                <span>{{ microphoneEnabled ? 'MIC AKTIV' : 'MIC OFF' }}</span>
                <strong>{{ microphoneEnabled ? selectedMicrophoneLabel() : 'Mikrofon deaktiviert' }}</strong>
              </div>
              <div class="mic-meter" aria-label="Mikrofon Pegel">
                <i [style.width.%]="microphoneEnabled ? micLevel() : 0"></i>
              </div>
            </div>
          </article>
        </section>

        <section class="room-participants-section">
          <div class="room-section-head">
            <div>
              <small>Lobby</small>
              <h2>Teilnehmer</h2>
            </div>
            <span>{{ participants().length }} verbunden</span>
          </div>

          <div class="room-participant-grid">
            @for (participant of participants(); track participant.id) {
              <article class="participant-card card accent-card" [class.moderator-card]="participant.role === 'moderator'">
                @if (participant.avatarImageDataUrl) {
                  <img class="participant-avatar image-avatar" [src]="participant.avatarImageDataUrl" [alt]="participant.name" />
                } @else {
                  <div class="participant-avatar">{{ participant.name.slice(0, 1).toUpperCase() }}</div>
                }
                <div>
                  <h3>{{ participant.name }}</h3>
                  <p>{{ participant.role === 'moderator' ? 'Moderator' : 'Mitspieler' }}</p>
                </div>
                <div class="participant-devices">
                  <span [class.off]="!participant.cameraEnabled">{{ participant.avatarImageDataUrl && !participant.cameraEnabled ? 'Bild' : 'Cam' }}</span>
                  <span [class.off]="!participant.microphoneEnabled">Mic</span>
                </div>
              </article>
            } @empty {
              <article class="card accent-card room-state-card"><h2>Noch niemand in der Lobby</h2></article>
            }
          </div>
        </section>
        }
      }
    </section>
  `
})
export class ShowLobbyRoomComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('previewVideo') private readonly previewVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('moderatorGameVideo') private readonly moderatorGameVideo?: ElementRef<HTMLVideoElement>;
  @ViewChildren('participantVideo') private readonly participantVideos?: QueryList<ElementRef<HTMLVideoElement>>;

  private readonly route = inject(ActivatedRoute);
  private readonly lobby = inject(ShowLobbyService);
  private readonly auth = inject(AuthService);

  readonly show = signal<PreparedShow | null>(null);
  readonly participants = signal<LobbyParticipant[]>([]);
  readonly cameraOptions = signal<DeviceOption[]>([
    { deviceId: 'default', label: 'Standardkamera' },
    { deviceId: 'none', label: 'Keine Kamera / Bild verwenden' }
  ]);
  readonly microphoneOptions = signal<DeviceOption[]>([{ deviceId: 'default', label: 'Standardmikrofon' }]);
  readonly isLoading = signal(true);
  readonly errorMessage = signal('');
  readonly mediaError = signal('');
  readonly lobbyError = signal('');
  readonly hasPreviewStream = signal(false);
  readonly micLevel = signal(0);
  readonly roomStatus = signal<LobbyStatus>('waiting');
  readonly socketStatus = signal<SocketStatus>('offline');
  readonly remoteStreams = signal<Record<string, MediaStream>>({});
  readonly currentParticipant = computed(() => this.participants().find(participant => participant.id === this.participantId) ?? null);
  readonly quizModerator = computed(() => this.participants().find(participant => participant.role === 'moderator') ?? null);
  readonly quizPlayers = computed(() => this.participants().filter(participant => participant.role !== 'moderator'));
  readonly leftQuizPlayers = computed(() => this.quizPlayers().slice(0, Math.ceil(this.quizPlayers().length / 2)));
  readonly rightQuizPlayers = computed(() => this.quizPlayers().slice(Math.ceil(this.quizPlayers().length / 2)));
  readonly activeQuizQuestion = computed(() => this.show()?.selectedQuestions?.[this.activeQuestionIndex()] ?? null);
  readonly quizRevealStage = signal<QuizRevealStage>('hidden');
  readonly activeQuestionIndex = signal(0);
  readonly currentTurnPlayerId = signal('');
  readonly currentPot = signal(0);
  readonly gamePhase = signal<QuizGamePhase>('idle');
  readonly smallBlindPlayerId = signal('');
  readonly bigBlindPlayerId = signal('');
  readonly bettingRound = signal(0);
  readonly currentBet = signal(0);
  readonly revealedEstimates = signal(false);
  readonly dealerOffset = signal(0);
  readonly smallBlindValue = signal(100);
  readonly bigBlindValue = signal(200);
  readonly isEditingBlinds = signal(false);
  readonly winnerPlayerIds = signal<string[]>([]);

  smallBlindDraft = '100';
  bigBlindDraft = '200';

  joinName = '';
  joinRole: LobbyParticipantRole = 'player';
  selectedCamera = 'default';
  selectedMicrophone = 'default';
  cameraEnabled = true;
  microphoneEnabled = true;
  avatarImageDataUrl: string | null = null;
  raiseAmount = 50;
  private readonly chipOverrides = new Map<string, number>();
  private readonly estimateValues = new Map<string, string>();
  private readonly currentRoundBets = new Map<string, number>();
  private readonly foldedPlayerIds = new Set<string>();
  private readonly outPlayerIds = new Set<string>();
  private readonly actedPlayerIds = new Set<string>();
  private lastAggressorPlayerId = '';
  private readonly gameStateHistory: QuizpokerGameState[] = [];
  private lastAppliedGameStateJson = '';

  private readonly participantId = this.getOrCreateParticipantId();
  private previewStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyserFrameId = 0;
  private previewReady = false;
  private socket: WebSocket | null = null;
  private readonly peerConnections = new Map<string, RTCPeerConnection>();
  private readonly offerStartedPeers = new Set<string>();

  ngOnInit(): void {
    this.joinName = this.auth.activeUser().username === 'Gast' ? '' : this.auth.activeUser().username;
    void this.loadDeviceOptions(false);
    this.loadRoom();
  }

  ngAfterViewInit(): void {
    this.previewReady = true;
    void this.refreshPreview();
  }

  ngAfterViewChecked(): void {
    this.bindModeratorGameVideo();
    this.bindParticipantVideos();
  }

  ngOnDestroy(): void {
    this.sendSocket({ type: 'leave', participantId: this.participantId });
    this.socket?.close();
    this.closeAllPeerConnections();
    this.stopPreview();
  }

  async joinRoom(): Promise<void> {
    const name = this.joinName.trim() || 'Gast';

    if (this.joinRole === 'moderator' && this.hasOtherModerator()) {
      this.joinRole = 'player';
    }

    const previewSnapshot = await this.capturePreviewFrameIfPossible();

    const participant: LobbyParticipant = {
      id: this.participantId,
      name,
      role: this.joinRole,
      cameraEnabled: this.cameraEnabled && this.selectedCamera !== 'none',
      microphoneEnabled: this.microphoneEnabled,
      cameraDeviceId: this.selectedCamera,
      microphoneDeviceId: this.selectedMicrophone,
      cameraDeviceLabel: this.selectedCameraLabel(),
      microphoneDeviceLabel: this.selectedMicrophoneLabel(),
      avatarImageDataUrl: this.avatarImageDataUrl || previewSnapshot,
      joinedAt: new Date().toISOString()
    };

    this.sendSocket({ type: 'join', participant });
    setTimeout(() => this.reconcileWebRtcPeers(), 150);
  }

  hasOtherModerator(): boolean {
    return this.participants().some(participant => participant.role === 'moderator' && participant.id !== this.participantId);
  }

  participantIdValue(): string {
    return this.participantId;
  }

  hasLiveVideo(participantId: string): boolean {
    if (!participantId) return false;
    if (participantId === this.participantId) {
      return !!this.previewStream?.getVideoTracks().length && this.cameraEnabled && this.selectedCamera !== 'none';
    }

    return !!this.remoteStreams()[participantId]?.getVideoTracks().length;
  }

  startShow(): void {
    if (this.currentParticipant()?.role !== 'moderator') {
      return;
    }

    this.sendSocket({
      type: 'startShow',
      participantId: this.participantId,
      gameState: this.createQuizpokerGameState()
    });
  }

  shouldShowLocalModeratorVideo(): boolean {
    return this.quizModerator()?.id === this.participantId
      && this.cameraEnabled
      && this.selectedCamera !== 'none'
      && this.hasPreviewStream();
  }


  isQuestionVisible(): boolean {
    return ['question', 'hint1', 'hint2', 'solution'].includes(this.quizRevealStage());
  }

  isHint1Visible(): boolean {
    return ['hint1', 'hint2', 'solution'].includes(this.quizRevealStage());
  }

  isHint2Visible(): boolean {
    return ['hint2', 'solution'].includes(this.quizRevealStage());
  }

  isSolutionVisible(): boolean {
    return this.quizRevealStage() === 'solution' || this.revealedEstimates();
  }

  canModeratorRevealQuestion(): boolean {
    return ['idle', 'payout'].includes(this.gamePhase());
  }

  revealQuestion(): void {
    if (!this.canModeratorRevealQuestion()) return;
    this.rememberGameState();
    this.startNewQuizpokerQuestion();
    this.syncQuizpokerState();
  }

  revealHint1(): void {
    if (this.gamePhase() !== 'waitingHint1') return;
    this.rememberGameState();
    this.quizRevealStage.set('hint1');
    this.startBettingRound(1);
    this.syncQuizpokerState();
  }

  revealHint2(): void {
    if (this.gamePhase() !== 'waitingHint2') return;
    this.rememberGameState();
    this.quizRevealStage.set('hint2');
    this.startBettingRound(2);
    this.syncQuizpokerState();
  }

  revealSolution(): void {
    if (this.gamePhase() !== 'waitingSolution') return;
    this.rememberGameState();
    this.quizRevealStage.set('solution');
    this.startBettingRound(3);
    this.syncQuizpokerState();
  }

  distributePot(): void {
    if (!['waitingPayout', 'waitingHint1', 'waitingHint2', 'waitingSolution'].includes(this.gamePhase())) return;
    if (!this.onlyOnePlayerLeft() && this.gamePhase() !== 'waitingPayout') return;
    this.rememberGameState();

    const activePlayers = this.quizPlayers().filter(player => !this.foldedPlayerIds.has(player.id));
    const solutionValue = Number(this.activeQuizQuestion()?.data?.['solution_value']);
    const validPlayers = activePlayers
      .map(player => ({ player, estimate: Number(String(this.estimateValues.get(player.id) ?? '').replace(',', '.')) }))
      .filter(entry => !Number.isNaN(entry.estimate));

    if (activePlayers.length === 1) {
      const soleWinner = activePlayers[0];
      this.chipOverrides.set(soleWinner.id, this.chipsFor(soleWinner.id) + this.currentPot());
      this.winnerPlayerIds.set([soleWinner.id]);
      this.currentPot.set(0);
      this.gamePhase.set('payout');
      this.revealedEstimates.set(true);
      this.markPlayersOutWithNoChips();
      this.syncQuizpokerState();
      return;
    }

    if (!validPlayers.length || Number.isNaN(solutionValue)) {
      this.winnerPlayerIds.set([]);
      this.currentPot.set(0);
      this.gamePhase.set('payout');
      this.revealedEstimates.set(true);
      this.markPlayersOutWithNoChips();
      this.syncQuizpokerState();
      return;
    }

    const bestDistance = Math.min(...validPlayers.map(entry => Math.abs(entry.estimate - solutionValue)));
    const winners = validPlayers.filter(entry => Math.abs(entry.estimate - solutionValue) === bestDistance);
    const share = Math.floor(this.currentPot() / Math.max(1, winners.length));

    for (const winner of winners) {
      this.chipOverrides.set(winner.player.id, this.chipsFor(winner.player.id) + share);
    }

    this.winnerPlayerIds.set(winners.map(winner => winner.player.id));
    this.currentPot.set(0);
    this.gamePhase.set('payout');
    this.revealedEstimates.set(true);
    this.markPlayersOutWithNoChips();
    this.syncQuizpokerState();
  }

  chipsFor(participantId: string): number {
    return this.chipOverrides.get(participantId) ?? 1000;
  }

  setChips(participantId: string, value: number | string): void {
    if (!participantId) return;
    this.rememberGameState();
    const parsed = Math.max(0, Number(value) || 0);
    this.chipOverrides.set(participantId, parsed);
    if (parsed > 0) {
      this.outPlayerIds.delete(participantId);
    } else if (['idle', 'payout'].includes(this.gamePhase())) {
      this.outPlayerIds.add(participantId);
    }
    this.syncQuizpokerState();
  }

  setBlindValues(smallBlind: number | string, bigBlind: number | string): void {
    if (this.currentParticipant()?.role !== 'moderator') return;
    this.rememberGameState();
    const small = Math.max(0, Number(smallBlind) || 0);
    const big = Math.max(0, Number(bigBlind) || 0);
    this.smallBlindValue.set(small);
    this.bigBlindValue.set(big);
    this.smallBlindDraft = String(small);
    this.bigBlindDraft = String(big);
    this.isEditingBlinds.set(false);
    this.syncQuizpokerState();
  }

  applyBlindDraft(): void {
    this.setBlindValues(this.smallBlindDraft, this.bigBlindDraft);
  }

  private syncBlindDraftsFromState(): void {
    if (this.isEditingBlinds()) return;
    this.smallBlindDraft = String(this.smallBlindValue());
    this.bigBlindDraft = String(this.bigBlindValue());
  }

  canUndo(): boolean {
    return this.currentParticipant()?.role === 'moderator' && this.gameStateHistory.length > 0;
  }

  undoLastAction(): void {
    if (this.currentParticipant()?.role !== 'moderator') return;
    const previous = this.gameStateHistory.pop();
    if (!previous) return;
    this.applyQuizpokerGameState(previous, false);
    this.syncQuizpokerState();
  }

  setCurrentTurnPlayer(participantId: string): void {
    this.rememberGameState();
    this.currentTurnPlayerId.set(participantId);
    this.syncQuizpokerState();
  }

  estimateFor(participantId: string): string {
    return this.estimateValues.get(participantId) ?? '';
  }

  estimateDisplayFor(participantId: string): string {
    if (this.currentParticipant()?.role === 'moderator' || participantId === this.participantId || this.revealedEstimates()) {
      return this.estimateFor(participantId);
    }
    return this.estimateFor(participantId) ? 'Schätzung abgegeben' : '';
  }

  canEditEstimate(participantId: string): boolean {
    return this.gamePhase() === 'estimating' && participantId === this.participantId && this.currentParticipant()?.role !== 'moderator';
  }

  setEstimate(participantId: string, value: string): void {
    if (!this.canEditEstimate(participantId) && this.currentParticipant()?.role !== 'moderator') return;
    this.estimateValues.set(participantId, value);
  }

  submitOwnEstimate(): void {
    const current = this.currentParticipant();
    if (!current || current.role === 'moderator' || this.gamePhase() !== 'estimating') return;
    if (!String(this.estimateValues.get(current.id) ?? '').trim()) return;

    if (this.allActivePlayersEstimated()) {
      this.startBettingRound(0);
    }
    this.syncQuizpokerState();
  }

  canSubmitEstimate(): boolean {
    const current = this.currentParticipant();
    return !!current
      && current.role !== 'moderator'
      && this.gamePhase() === 'estimating'
      && !!String(this.estimateValues.get(current.id) ?? '').trim();
  }

  isOwnTurn(): boolean {
    return this.gamePhase() === 'betting' && this.currentTurnPlayerId() === this.participantId && this.currentParticipant()?.role !== 'moderator';
  }

  checkCallLabel(): string {
    const callAmount = this.amountToCall(this.participantId);
    if (callAmount <= 0) return 'Check';

    const available = this.chipsFor(this.participantId);
    return available < callAmount ? `All-In ${available}` : `Call ${callAmount}`;
  }

  playerActionHint(): string {
    if (this.gamePhase() === 'estimating') return 'Schätzung eingeben und abgeben.';
    if (this.gamePhase() === 'betting') return `Am Zug: ${this.participantName(this.currentTurnPlayerId())}`;
    return 'Warte auf den Moderator.';
  }

  checkOrCall(): void {
    if (!this.isOwnTurn()) return;
    this.rememberGameState();
    this.payToPot(this.participantId, this.amountToCall(this.participantId));
    this.finishBettingAction(this.participantId);
  }

  fold(): void {
    if (!this.isOwnTurn()) return;
    this.rememberGameState();
    this.foldedPlayerIds.add(this.participantId);
    this.finishBettingAction(this.participantId);
  }

  raise(): void {
    if (!this.isOwnTurn()) return;
    this.rememberGameState();

    const raise = Math.max(0, Number(this.raiseAmount) || 0);
    const callAmount = this.amountToCall(this.participantId);
    const beforeBet = this.currentRoundBets.get(this.participantId) ?? 0;
    const beforeCurrentBet = this.currentBet();

    this.payToPot(this.participantId, callAmount + raise);

    const afterBet = this.currentRoundBets.get(this.participantId) ?? 0;
    const effectiveBet = Math.max(beforeCurrentBet, afterBet);

    if (effectiveBet > beforeCurrentBet) {
      this.currentBet.set(effectiveBet);
      this.markNewAggressor(this.participantId);
    } else if (afterBet > beforeBet && this.chipsFor(this.participantId) === 0) {
      // All-In ohne vollständigen Raise. Der Spieler bleibt im Pot, erhöht aber nicht den Call-Wert.
    }

    this.finishBettingAction(this.participantId);
  }

  isFolded(participantId: string): boolean {
    return this.foldedPlayerIds.has(participantId);
  }

  isPlayerOut(participantId: string): boolean {
    return this.outPlayerIds.has(participantId);
  }

  quizQuestionText(): string {
    return this.activeQuizQuestion()?.questionText || 'Noch keine Frage ausgewählt.';
  }

  quizHint(index: 1 | 2): string {
    const question = this.activeQuizQuestion();
    const value = question?.data?.[`hint_${index}`];
    return typeof value === 'string' && value.trim() ? value : `Hint ${index} wird später angezeigt.`;
  }

  quizSolution(): string {
    const question = this.activeQuizQuestion();
    if (!question) return 'Lösung wird später angezeigt.';

    const solutionText = question.solutionText || question.data?.['solution_text'];
    if (solutionText !== undefined && solutionText !== null && String(solutionText).trim()) {
      return String(solutionText);
    }

    const solutionValue = question.data?.['solution_value'];
    return solutionValue !== undefined && solutionValue !== null && String(solutionValue).trim()
      ? String(solutionValue)
      : 'Lösung wird später angezeigt.';
  }

  shouldShowWinnerCrown(participantId: string): boolean {
    if (!this.winnerPlayerIds().includes(participantId)) return false;
    return this.currentParticipant()?.role === 'moderator' || this.revealedEstimates();
  }

  private startNewQuizpokerQuestion(): void {
    this.markPlayersOutWithNoChips();
    const allPlayers = this.quizPlayers();
    const players = this.eligibleQuizPlayersForNewRound();
    if (!players.length) return;

    if (this.gamePhase() === 'payout') {
      const questionCount = this.show()?.selectedQuestions?.length ?? 1;
      this.activeQuestionIndex.set((this.activeQuestionIndex() + 1) % Math.max(1, questionCount));
      this.dealerOffset.set((this.dealerOffset() + 1) % Math.max(1, allPlayers.length));
    }

    this.quizRevealStage.set('question');
    this.gamePhase.set('estimating');
    this.revealedEstimates.set(false);
    this.currentPot.set(0);
    this.winnerPlayerIds.set([]);
    this.currentBet.set(this.bigBlindValue());
    this.bettingRound.set(0);
    this.currentRoundBets.clear();
    this.foldedPlayerIds.clear();
    this.actedPlayerIds.clear();
    this.estimateValues.clear();

    for (const player of allPlayers) {
      if (!this.chipOverrides.has(player.id)) {
        this.chipOverrides.set(player.id, 1000);
      }
    }

    const smallBlindIndex = this.findNextEligibleIndexFromDealer(players);
    const bigBlindIndex = players.length > 1 ? (smallBlindIndex + 1) % players.length : -1;
    const smallBlindId = players[smallBlindIndex]?.id ?? '';
    const bigBlindId = bigBlindIndex >= 0 ? players[bigBlindIndex]?.id ?? '' : '';
    this.smallBlindPlayerId.set(smallBlindId);
    this.bigBlindPlayerId.set(bigBlindId);

    this.payToPot(smallBlindId, this.smallBlindValue());
    if (bigBlindId && bigBlindId !== smallBlindId) {
      this.payToPot(bigBlindId, this.bigBlindValue());
    }
    this.currentTurnPlayerId.set(bigBlindIndex >= 0 ? players[(bigBlindIndex + 1) % players.length]?.id ?? bigBlindId : smallBlindId);
  }

  private startBettingRound(round: number): void {
    const players = this.activeBettingPlayers();
    if (!players.length) return;

    this.bettingRound.set(round);
    const previousRoundBets = new Map(this.currentRoundBets);
    this.actedPlayerIds.clear();
    this.currentRoundBets.clear();
    this.lastAggressorPlayerId = '';

    if (players.length <= 1) {
      this.endBettingRound();
      return;
    }

    this.gamePhase.set('betting');
    this.currentBet.set(round === 0 ? this.bigBlindValue() : 0);

    if (round === 0) {
      if (this.smallBlindPlayerId()) this.currentRoundBets.set(this.smallBlindPlayerId(), previousRoundBets.get(this.smallBlindPlayerId()) ?? this.smallBlindValue());
      if (this.bigBlindPlayerId()) this.currentRoundBets.set(this.bigBlindPlayerId(), previousRoundBets.get(this.bigBlindPlayerId()) ?? this.bigBlindValue());
      this.lastAggressorPlayerId = this.bigBlindPlayerId();
    }

    const firstTurn = this.playerAfterBigBlind() || players[0]?.id || '';
    this.currentTurnPlayerId.set(this.nextPlayerNeedingActionFrom(firstTurn) || firstTurn);
  }

  private finishBettingAction(participantId: string): void {
    this.actedPlayerIds.add(participantId);

    if (this.activeBettingPlayers().length <= 1 || this.isBettingRoundComplete()) {
      this.endBettingRound();
      this.syncQuizpokerState();
      return;
    }

    const next = this.nextPlayerNeedingActionAfter(participantId);
    if (!next || next === participantId) {
      this.endBettingRound();
    } else {
      this.currentTurnPlayerId.set(next);
    }
    this.syncQuizpokerState();
  }

  private endBettingRound(): void {
    const round = this.bettingRound();
    this.currentTurnPlayerId.set('');
    if (round === 0) this.gamePhase.set('waitingHint1');
    else if (round === 1) this.gamePhase.set('waitingHint2');
    else if (round === 2) this.gamePhase.set('waitingSolution');
    else this.gamePhase.set('waitingPayout');
  }

  private allActivePlayersEstimated(): boolean {
    return this.quizPlayers()
      .filter(player => !this.foldedPlayerIds.has(player.id) && !this.outPlayerIds.has(player.id) && this.chipsFor(player.id) > 0)
      .every(player => String(this.estimateValues.get(player.id) ?? '').trim());
  }

  private amountToCall(participantId: string): number {
    return Math.max(0, this.currentBet() - (this.currentRoundBets.get(participantId) ?? 0));
  }

  private markNewAggressor(participantId: string): void {
    this.lastAggressorPlayerId = participantId;
    this.actedPlayerIds.clear();
    this.actedPlayerIds.add(participantId);
  }

  private isBettingRoundComplete(): boolean {
    const players = this.activeBettingPlayers();
    if (players.length <= 1) return true;

    return players.every(player => {
      const alreadyActed = this.actedPlayerIds.has(player.id);
      const hasMatchedCurrentBet = (this.currentRoundBets.get(player.id) ?? 0) >= this.currentBet();
      const isAllIn = this.chipsFor(player.id) <= 0;
      return alreadyActed && (hasMatchedCurrentBet || isAllIn);
    });
  }

  private nextPlayerNeedingActionAfter(participantId: string): string {
    const players = this.activeBettingPlayers();
    if (!players.length) return '';
    const startIndex = players.findIndex(player => player.id === participantId);

    for (let offset = 1; offset <= players.length; offset++) {
      const candidate = players[(startIndex + offset + players.length) % players.length];
      if (this.playerNeedsBettingAction(candidate.id)) {
        return candidate.id;
      }
    }

    return '';
  }

  private nextPlayerNeedingActionFrom(participantId: string): string {
    const players = this.activeBettingPlayers();
    if (!players.length) return '';
    const startIndex = Math.max(0, players.findIndex(player => player.id === participantId));

    for (let offset = 0; offset < players.length; offset++) {
      const candidate = players[(startIndex + offset + players.length) % players.length];
      if (this.playerNeedsBettingAction(candidate.id)) {
        return candidate.id;
      }
    }

    return '';
  }

  private playerNeedsBettingAction(participantId: string): boolean {
    if (!participantId || this.foldedPlayerIds.has(participantId) || this.outPlayerIds.has(participantId)) return false;
    if (this.chipsFor(participantId) <= 0) return false;

    const alreadyActed = this.actedPlayerIds.has(participantId);
    const hasMatchedCurrentBet = (this.currentRoundBets.get(participantId) ?? 0) >= this.currentBet();
    return !alreadyActed || !hasMatchedCurrentBet;
  }

  private payToPot(participantId: string, amount: number): void {
    if (!participantId || amount <= 0) return;
    const available = this.chipsFor(participantId);
    const payment = Math.min(available, amount);
    this.chipOverrides.set(participantId, available - payment);
    this.currentRoundBets.set(participantId, (this.currentRoundBets.get(participantId) ?? 0) + payment);
    this.currentPot.set(this.currentPot() + payment);
  }

  private playerAfterBigBlind(): string {
    const players = this.activeBettingPlayers();
    const bigBlindIndex = players.findIndex(player => player.id === this.bigBlindPlayerId());
    return players[(bigBlindIndex + 1 + players.length) % players.length]?.id ?? players[0]?.id ?? '';
  }

  private nextActivePlayerAfter(participantId: string): string {
    const players = this.activeBettingPlayers();
    if (!players.length) return '';
    const currentIndex = players.findIndex(player => player.id === participantId);
    return players[(currentIndex + 1 + players.length) % players.length]?.id ?? '';
  }

  private activeBettingPlayers(): LobbyParticipant[] {
    return this.quizPlayers().filter(player => !this.foldedPlayerIds.has(player.id) && !this.outPlayerIds.has(player.id));
  }

  private eligibleQuizPlayersForNewRound(): LobbyParticipant[] {
    return this.quizPlayers().filter(player => !this.outPlayerIds.has(player.id) && this.chipsFor(player.id) > 0);
  }

  private findNextEligibleIndexFromDealer(players: LobbyParticipant[]): number {
    if (!players.length) return 0;
    const allPlayers = this.quizPlayers();
    const dealerId = allPlayers[this.dealerOffset() % Math.max(1, allPlayers.length)]?.id;
    const directIndex = players.findIndex(player => player.id === dealerId);
    return directIndex >= 0 ? directIndex : 0;
  }

  private markPlayersOutWithNoChips(): void {
    for (const player of this.quizPlayers()) {
      if (this.chipsFor(player.id) <= 0) {
        this.outPlayerIds.add(player.id);
      }
    }
  }

  private participantName(participantId: string): string {
    return this.participants().find(participant => participant.id === participantId)?.name ?? 'niemand';
  }

  private syncQuizpokerState(): void {
    if (this.roomStatus() !== 'started' || this.show()?.format !== 'quizpoker') {
      return;
    }

    this.sendSocket({
      type: 'updateGameState',
      participantId: this.participantId,
      gameState: this.createQuizpokerGameState()
    });
  }

  private createQuizpokerGameState(): QuizpokerGameState {
    const firstPlayerId = this.quizPlayers()[0]?.id ?? '';
    const currentTurnPlayerId = this.currentTurnPlayerId() || firstPlayerId;

    return {
      revealStage: this.quizRevealStage(),
      gamePhase: this.gamePhase(),
      activeQuestionIndex: this.activeQuestionIndex(),
      currentTurnPlayerId,
      currentPot: this.currentPot(),
      chips: this.recordFromMap(this.chipOverrides),
      estimates: this.recordFromMap(this.estimateValues),
      smallBlindPlayerId: this.smallBlindPlayerId(),
      bigBlindPlayerId: this.bigBlindPlayerId(),
      bettingRound: this.bettingRound(),
      currentBet: this.currentBet(),
      currentRoundBets: this.recordFromMap(this.currentRoundBets),
      foldedPlayerIds: Array.from(this.foldedPlayerIds),
      outPlayerIds: Array.from(this.outPlayerIds),
      actedPlayerIds: Array.from(this.actedPlayerIds),
      revealedEstimates: this.revealedEstimates(),
      dealerOffset: this.dealerOffset(),
      smallBlindValue: this.smallBlindValue(),
      bigBlindValue: this.bigBlindValue(),
      winnerPlayerIds: this.winnerPlayerIds()
    };
  }

  private applyQuizpokerGameState(gameState: QuizpokerGameState | null, captureHistory = true): void {
    if (!gameState) {
      return;
    }

    if (captureHistory && this.currentParticipant()?.role === 'moderator') {
      this.rememberIncomingStateForUndo(gameState);
    }

    this.quizRevealStage.set(gameState.revealStage ?? 'hidden');
    this.gamePhase.set(gameState.gamePhase ?? 'idle');
    this.activeQuestionIndex.set(Math.max(0, Number(gameState.activeQuestionIndex ?? 0)));
    this.currentTurnPlayerId.set(gameState.currentTurnPlayerId ?? '');
    this.currentPot.set(Math.max(0, Number(gameState.currentPot ?? 0)));
    this.smallBlindPlayerId.set(gameState.smallBlindPlayerId ?? '');
    this.bigBlindPlayerId.set(gameState.bigBlindPlayerId ?? '');
    this.bettingRound.set(Math.max(0, Number(gameState.bettingRound ?? 0)));
    this.currentBet.set(Math.max(0, Number(gameState.currentBet ?? 0)));
    this.revealedEstimates.set(!!gameState.revealedEstimates);
    this.dealerOffset.set(Math.max(0, Number(gameState.dealerOffset ?? 0)));
    this.smallBlindValue.set(Math.max(0, Number(gameState.smallBlindValue ?? 100)));
    this.bigBlindValue.set(Math.max(0, Number(gameState.bigBlindValue ?? 200)));
    this.syncBlindDraftsFromState();
    this.winnerPlayerIds.set(gameState.winnerPlayerIds ?? []);

    this.chipOverrides.clear();
    Object.entries(gameState.chips ?? {}).forEach(([participantId, chips]) => {
      this.chipOverrides.set(participantId, Math.max(0, Number(chips) || 0));
    });

    this.estimateValues.clear();
    Object.entries(gameState.estimates ?? {}).forEach(([participantId, estimate]) => {
      this.estimateValues.set(participantId, String(estimate ?? ''));
    });

    this.currentRoundBets.clear();
    Object.entries(gameState.currentRoundBets ?? {}).forEach(([participantId, bet]) => {
      this.currentRoundBets.set(participantId, Math.max(0, Number(bet) || 0));
    });

    this.foldedPlayerIds.clear();
    (gameState.foldedPlayerIds ?? []).forEach(participantId => this.foldedPlayerIds.add(participantId));

    this.outPlayerIds.clear();
    (gameState.outPlayerIds ?? []).forEach(participantId => this.outPlayerIds.add(participantId));

    this.actedPlayerIds.clear();
    (gameState.actedPlayerIds ?? []).forEach(participantId => this.actedPlayerIds.add(participantId));
  }

  private onlyOnePlayerLeft(): boolean {
    return this.activeBettingPlayers().length <= 1;
  }

  private rememberGameState(): void {
    if (this.roomStatus() !== 'started' || this.show()?.format !== 'quizpoker') return;
    const snapshot = this.createQuizpokerGameState();
    this.gameStateHistory.push(JSON.parse(JSON.stringify(snapshot)) as QuizpokerGameState);
    if (this.gameStateHistory.length > 30) {
      this.gameStateHistory.shift();
    }
  }

  private rememberIncomingStateForUndo(incoming: QuizpokerGameState): void {
    const incomingJson = JSON.stringify(incoming);
    if (!this.lastAppliedGameStateJson) {
      this.lastAppliedGameStateJson = incomingJson;
      return;
    }

    if (incomingJson === this.lastAppliedGameStateJson) {
      return;
    }

    const current = this.createQuizpokerGameState();
    const currentJson = JSON.stringify(current);
    if (currentJson !== incomingJson) {
      this.gameStateHistory.push(JSON.parse(JSON.stringify(current)) as QuizpokerGameState);
      if (this.gameStateHistory.length > 30) {
        this.gameStateHistory.shift();
      }
    }

    this.lastAppliedGameStateJson = incomingJson;
  }

  private recordFromMap<T extends string | number>(map: Map<string, T>): Record<string, T> {
    return Array.from(map.entries()).reduce<Record<string, T>>((record, [key, value]) => {
      record[key] = value;
      return record;
    }, {});
  }

  formatTitle(formatId: string): string {
    return this.lobby.formats.find(format => format.id === formatId)?.title ?? formatId;
  }

  selectedCameraLabel(): string {
    return this.cameraOptions().find(camera => camera.deviceId === this.selectedCamera)?.label ?? 'Standardkamera';
  }

  selectedMicrophoneLabel(): string {
    return this.microphoneOptions().find(microphone => microphone.deviceId === this.selectedMicrophone)?.label ?? 'Standardmikrofon';
  }

  canUseImageFallback(): boolean {
    return !this.cameraEnabled || this.selectedCamera === 'none' || !!this.mediaError();
  }

  handleAvatarUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.mediaError.set('Bitte eine Bilddatei auswählen.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.avatarImageDataUrl = typeof reader.result === 'string' ? reader.result : null;
    };
    reader.readAsDataURL(file);
  }

  toggleCamera(): void {
    this.cameraEnabled = !this.cameraEnabled;
    void this.refreshPreview();
  }

  toggleMicrophone(): void {
    this.microphoneEnabled = !this.microphoneEnabled;
    void this.refreshPreview();
  }

  async refreshPreview(): Promise<void> {
    if (!this.previewReady) {
      return;
    }

    this.stopPreview();
    this.mediaError.set('');

    const useCamera = this.cameraEnabled && this.selectedCamera !== 'none';
    const useMicrophone = this.microphoneEnabled;

    if (!useCamera && !useMicrophone) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.mediaError.set('Dein Browser unterstützt keine Kamera-/Mikrofon-Vorschau.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: useCamera ? this.videoDeviceConstraint(this.selectedCamera) : false,
        audio: useMicrophone ? this.deviceConstraint(this.selectedMicrophone) : false
      });

      this.previewStream = stream;
      this.hasPreviewStream.set(stream.getVideoTracks().length > 0);

      if (this.previewVideo?.nativeElement) {
        this.previewVideo.nativeElement.srcObject = stream;
      }

      await this.loadDeviceOptions(true);
      this.startMicrophoneMeter(stream);
      this.restartWebRtcWithCurrentPeers();
    } catch {
      this.hasPreviewStream.set(false);
      this.mediaError.set('Kamera oder Mikrofon konnte nicht geöffnet werden. Bitte Browser-Berechtigungen prüfen oder ein Bild verwenden.');
    }
  }


  private async capturePreviewFrameIfPossible(): Promise<string | null> {
    const video = this.previewVideo?.nativeElement;
    if (!video || !this.cameraEnabled || this.selectedCamera === 'none' || !this.hasPreviewStream()) {
      return null;
    }

    if (!video.videoWidth || !video.videoHeight) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 360;
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    const sourceRatio = video.videoWidth / video.videoHeight;
    const targetRatio = canvas.width / canvas.height;
    let drawWidth = video.videoWidth;
    let drawHeight = video.videoHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (sourceRatio > targetRatio) {
      drawWidth = video.videoHeight * targetRatio;
      sourceX = (video.videoWidth - drawWidth) / 2;
    } else if (sourceRatio < targetRatio) {
      drawHeight = video.videoWidth / targetRatio;
      sourceY = (video.videoHeight - drawHeight) / 2;
    }

    context.drawImage(video, sourceX, sourceY, drawWidth, drawHeight, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.72);
  }

  private bindModeratorGameVideo(): void {
    const video = this.moderatorGameVideo?.nativeElement;
    if (!video || !this.previewStream || !this.shouldShowLocalModeratorVideo()) {
      return;
    }

    if (video.srcObject !== this.previewStream) {
      video.srcObject = this.previewStream;
    }
  }

  private connectSocket(showId: string): void {
    this.socket?.close();
    this.socketStatus.set('connecting');
    this.lobbyError.set('');

    const socket = new WebSocket(`${appEnvironment.wsBaseUrl}/show-lobbies/${showId}`);
    this.socket = socket;

    socket.onopen = () => {
      this.socketStatus.set('connected');
    };

    socket.onmessage = event => {
      const message = JSON.parse(event.data) as LobbySocketStateMessage | LobbySocketErrorMessage | LobbySocketWebRtcSignalMessage | { type: string };
      if (message.type === 'state') {
        const state = message as LobbySocketStateMessage;
        this.participants.set(state.participants ?? []);
        const firstPlayer = this.quizPlayers()[0]?.id ?? '';
        if (!this.currentTurnPlayerId() || !this.quizPlayers().some(player => player.id === this.currentTurnPlayerId())) {
          this.currentTurnPlayerId.set(firstPlayer);
        }
        this.roomStatus.set(state.status ?? 'waiting');
        this.applyQuizpokerGameState(state.gameState ?? null);
        this.prefillFromExistingParticipant();
        this.reconcileWebRtcPeers();
      }

      if (message.type === 'webrtcSignal') {
        void this.handleWebRtcSignal(message as LobbySocketWebRtcSignalMessage);
      }

      if (message.type === 'error') {
        this.lobbyError.set((message as LobbySocketErrorMessage).message);
      }
    };

    socket.onerror = () => {
      this.lobbyError.set('WebSocket-Verbindung konnte nicht hergestellt werden. Läuft das Backend?');
    };

    socket.onclose = () => {
      this.socketStatus.set('offline');
    };
  }

  private sendSocket(message: unknown): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.lobbyError.set('Noch nicht live verbunden. Bitte kurz warten und erneut versuchen.');
      return;
    }

    this.lobbyError.set('');
    this.socket.send(JSON.stringify(message));
  }

  private bindParticipantVideos(): void {
    const videos = this.participantVideos?.toArray() ?? [];

    for (const videoRef of videos) {
      const video = videoRef.nativeElement;
      const participantId = video.dataset['participantId'];
      if (!participantId) continue;

      const stream = this.streamForParticipant(participantId);
      if (stream && video.srcObject !== stream) {
        video.srcObject = stream;
      }
    }
  }

  private streamForParticipant(participantId: string): MediaStream | null {
    if (participantId === this.participantId) {
      return this.previewStream;
    }

    return this.remoteStreams()[participantId] ?? null;
  }

  private reconcileWebRtcPeers(): void {
    if (!this.currentParticipant() || !this.previewStream) {
      return;
    }

    const remoteParticipants = this.participants().filter(participant => participant.id !== this.participantId);
    const remoteIds = new Set(remoteParticipants.map(participant => participant.id));

    for (const peerId of Array.from(this.peerConnections.keys())) {
      if (!remoteIds.has(peerId)) {
        this.closePeerConnection(peerId);
      }
    }

    for (const participant of remoteParticipants) {
      const pc = this.ensurePeerConnection(participant.id);
      if (this.shouldCreateOffer(participant.id) && !this.offerStartedPeers.has(participant.id) && pc.signalingState === 'stable') {
        this.offerStartedPeers.add(participant.id);
        void this.createOfferForPeer(participant.id, pc);
      }
    }
  }

  private ensurePeerConnection(remoteParticipantId: string): RTCPeerConnection {
    const existing = this.peerConnections.get(remoteParticipantId);
    if (existing) {
      return existing;
    }

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' }
      ]
    });

    pc.onicecandidate = event => {
      if (event.candidate) {
        this.sendWebRtcSignal(remoteParticipantId, 'ice-candidate', event.candidate.toJSON());
      }
    };

    pc.ontrack = event => {
      const incomingStream = event.streams[0] ?? new MediaStream([event.track]);
      this.remoteStreams.update(streams => ({ ...streams, [remoteParticipantId]: incomingStream }));
      setTimeout(() => this.bindParticipantVideos());
    };

    pc.onconnectionstatechange = () => {
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
        this.closePeerConnection(remoteParticipantId);
      }
    };

    this.previewStream?.getTracks().forEach(track => pc.addTrack(track, this.previewStream!));
    this.peerConnections.set(remoteParticipantId, pc);
    return pc;
  }

  private async createOfferForPeer(remoteParticipantId: string, pc: RTCPeerConnection): Promise<void> {
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.sendWebRtcSignal(remoteParticipantId, 'offer', offer);
    } catch {
      this.lobbyError.set('WebRTC Offer konnte nicht erstellt werden.');
    }
  }

  private async handleWebRtcSignal(message: LobbySocketWebRtcSignalMessage): Promise<void> {
    if (message.toParticipantId !== this.participantId || !message.fromParticipantId) {
      return;
    }

    const pc = this.ensurePeerConnection(message.fromParticipantId);

    try {
      if (message.signalType === 'offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(message.payload as RTCSessionDescriptionInit));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.sendWebRtcSignal(message.fromParticipantId, 'answer', answer);
        return;
      }

      if (message.signalType === 'answer') {
        await pc.setRemoteDescription(new RTCSessionDescription(message.payload as RTCSessionDescriptionInit));
        return;
      }

      if (message.signalType === 'ice-candidate') {
        await pc.addIceCandidate(new RTCIceCandidate(message.payload as RTCIceCandidateInit));
      }
    } catch {
      this.lobbyError.set('WebRTC-Verbindung konnte nicht vollständig aufgebaut werden.');
    }
  }

  private sendWebRtcSignal(toParticipantId: string, signalType: 'offer' | 'answer' | 'ice-candidate', payload: RTCSessionDescriptionInit | RTCIceCandidateInit): void {
    this.sendSocket({
      type: 'webrtcSignal',
      fromParticipantId: this.participantId,
      toParticipantId,
      signalType,
      payload
    });
  }

  private shouldCreateOffer(remoteParticipantId: string): boolean {
    return this.participantId.localeCompare(remoteParticipantId) < 0;
  }

  private restartWebRtcWithCurrentPeers(): void {
    if (!this.currentParticipant()) return;
    this.closeAllPeerConnections();
    setTimeout(() => this.reconcileWebRtcPeers(), 100);
  }

  private closePeerConnection(participantId: string): void {
    this.peerConnections.get(participantId)?.close();
    this.peerConnections.delete(participantId);
    this.offerStartedPeers.delete(participantId);
    this.remoteStreams.update(streams => {
      const next = { ...streams };
      delete next[participantId];
      return next;
    });
  }

  private closeAllPeerConnections(): void {
    for (const participantId of Array.from(this.peerConnections.keys())) {
      this.closePeerConnection(participantId);
    }
  }

  private videoDeviceConstraint(deviceId: string): boolean | MediaTrackConstraints {
    const base: MediaTrackConstraints = {
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      aspectRatio: { ideal: 16 / 9 }
    };

    if (!deviceId || deviceId === 'default') {
      return base;
    }

    return { ...base, deviceId: { exact: deviceId } };
  }

  private deviceConstraint(deviceId: string): boolean | MediaTrackConstraints {
    if (!deviceId || deviceId === 'default') {
      return true;
    }

    return { deviceId: { exact: deviceId } };
  }

  private stopPreview(): void {
    if (this.analyserFrameId) {
      cancelAnimationFrame(this.analyserFrameId);
      this.analyserFrameId = 0;
    }

    if (this.audioContext) {
      void this.audioContext.close();
      this.audioContext = null;
    }

    if (this.previewVideo?.nativeElement) {
      this.previewVideo.nativeElement.srcObject = null;
    }

    this.previewStream?.getTracks().forEach(track => track.stop());
    this.previewStream = null;
    this.hasPreviewStream.set(false);
    this.micLevel.set(0);
  }

  private startMicrophoneMeter(stream: MediaStream): void {
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length || !this.microphoneEnabled) {
      this.micLevel.set(0);
      return;
    }

    const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }

    this.audioContext = new AudioContextCtor();
    const source = this.audioContext.createMediaStreamSource(new MediaStream(audioTracks));
    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      const average = data.reduce((sum, value) => sum + value, 0) / data.length;
      this.micLevel.set(Math.min(100, Math.round((average / 140) * 100)));
      this.analyserFrameId = requestAnimationFrame(tick);
    };

    tick();
  }

  private loadRoom(): void {
    const showId = this.route.snapshot.paramMap.get('id');
    if (!showId) {
      this.errorMessage.set('Keine Lobby-ID gefunden.');
      this.isLoading.set(false);
      return;
    }

    this.lobby.getShow(showId).subscribe({
      next: show => {
        if (show.status !== 'active') {
          this.errorMessage.set('Diese Lobby ist nicht aktiv.');
          this.isLoading.set(false);
          return;
        }

        this.show.set(show);
        this.connectSocket(show.id);
        this.isLoading.set(false);
      },
      error: () => {
        this.errorMessage.set('Die Lobby konnte nicht vom Backend geladen werden.');
        this.isLoading.set(false);
      }
    });
  }

  private prefillFromExistingParticipant(): void {
    const existing = this.currentParticipant();
    if (!existing) {
      return;
    }

    this.joinName = existing.name;
    this.joinRole = existing.role;
    this.selectedCamera = existing.cameraDeviceId || 'default';
    this.selectedMicrophone = existing.microphoneDeviceId || 'default';
    this.cameraEnabled = existing.cameraEnabled || existing.cameraDeviceId === 'none';
    this.microphoneEnabled = existing.microphoneEnabled;
    this.avatarImageDataUrl = existing.avatarImageDataUrl ?? this.avatarImageDataUrl;
  }

  private async loadDeviceOptions(keepSelection: boolean): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices
        .filter(device => device.kind === 'videoinput')
        .map((device, index) => ({ deviceId: device.deviceId, label: device.label || `Kamera ${index + 1}` }))
        .filter(device => device.deviceId);

      const microphones = devices
        .filter(device => device.kind === 'audioinput')
        .map((device, index) => ({ deviceId: device.deviceId, label: device.label || `Mikrofon ${index + 1}` }))
        .filter(device => device.deviceId);

      this.cameraOptions.set([
        { deviceId: 'default', label: 'Standardkamera' },
        { deviceId: 'none', label: 'Keine Kamera / Bild verwenden' },
        ...cameras
      ]);
      this.microphoneOptions.set([{ deviceId: 'default', label: 'Standardmikrofon' }, ...microphones]);

      if (!keepSelection) {
        this.selectedCamera = 'default';
        this.selectedMicrophone = 'default';
      }
    } catch {
      // Fallback options stay active.
    }
  }

  private getOrCreateParticipantId(): string {
    const key = 'daniiplace.participantId';
    const existing = localStorage.getItem(key);
    if (existing) return existing;

    const id = `participant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(key, id);
    return id;
  }
}
